import { Alert, Button, Layout, Space, Tag } from 'antd';
import React, { useState } from 'react';
import styled from 'styled-components';

import {
    BackToModesLink,
    CORRECT_SCORE_THRESHOLD,
    EXTENDED_TOTAL_ROUNDS,
    Header,
    MapFrame,
    ModalMapPreview,
    ModalPhoto,
    OptionButton,
    OptionGrid,
    Page,
    Question,
    QuizFinalResult,
    QuizPanel,
    QuizPhotoPreview,
    QuizPhotoThumbnail,
    RoundResultModal,
    RoundStatus,
    SvgMap,
    calculateBlindMapScore,
    calculateDistanceKm,
    formatDistance,
    makeOptions,
    pickRandom,
    shuffleArray,
    useKrajFeatures,
} from '../../components/quiz';
import riversData from '../../data/rivers.json';

const { Content } = Layout;

const ITEMS = riversData.items.filter(r => r.lengthCz && r.destination);
const ITEMS_WITH_SOURCE = ITEMS.filter(r => r.source);
const ALL_DESTINATIONS = Array.from(new Set(ITEMS.map(r => r.destination))).sort((a, b) => a.localeCompare(b, 'cs'));

const QUESTION_TYPES = ['source', 'destination', 'longest'];

const RankCard = styled.button`
    text-align: left;
    background: ${({ $state }) => ($state === 'correct' ? '#f6ffed' : $state === 'wrong' ? '#fff1f0' : '#ffffff')};
    border: 2px solid ${({ $state }) => ($state === 'correct' ? '#52c41a' : $state === 'wrong' ? '#ff4d4f' : '#d9d9d9')};
    border-radius: 10px;
    padding: 16px 18px;
    cursor: ${({ $disabled }) => ($disabled ? 'default' : 'pointer')};
    transition: transform 0.12s ease, border-color 0.2s ease, background 0.2s ease;
    font-family: inherit;
    font-size: inherit;
    color: inherit;
    width: 100%;

    &:hover {
        transform: ${({ $disabled }) => ($disabled ? 'none' : 'translateY(-1px)')};
        border-color: ${({ $state, $disabled }) => ($disabled || $state ? undefined : '#c80707')};
    }
`;

const RankCardName = styled.div`
    font-size: clamp(18px, 2.4vw, 22px);
    font-weight: 700;
`;

const RankCardMeta = styled.div`
    font-size: 13px;
    color: #595959;
    margin-top: 4px;
`;

const RankCardStack = styled.div`
    display: grid;
    gap: 10px;
`;

const RankCardContent = styled.div`
    display: flex;
    align-items: center;
    gap: 12px;
    min-width: 0;
`;

const RankCardCopy = styled.div`
    min-width: 0;
`;

const Length = styled.span`
    font-weight: 700;
    color: #1f1f1f;
`;

const formatLength = km => `${km.toLocaleString('cs-CZ', { maximumFractionDigits: 1 })} km`;

const pickLongestSet = () => {
    const sorted = [...ITEMS].sort((a, b) => b.lengthCz - a.lengthCz);
    const n = sorted.length;
    if (n < 3) return shuffleArray(sorted.slice(0, 3));
    const top = sorted.slice(0, Math.max(1, Math.floor(n * 0.25)));
    const mid = sorted.slice(Math.floor(n * 0.25), Math.floor(n * 0.6));
    const low = sorted.slice(Math.floor(n * 0.6));
    const a = pickRandom(top);
    // Prevent duplicates if a band is empty and we fall back to the full list.
    const bPool = mid.length ? mid : sorted.filter(x => x !== a);
    let b = pickRandom(bPool);
    const cPool = low.length ? low : sorted.filter(x => x !== a && x !== b);
    let c = pickRandom(cPool);
    if (a.lengthCz - b.lengthCz < 20 && mid.length > 1) b = pickRandom(mid);
    if (b.lengthCz - c.lengthCz < 15 && low.length > 1) c = pickRandom(low);
    return shuffleArray([a, b, c]);
};

const makeQuestion = () => {
    const candidates = shuffleArray(QUESTION_TYPES);
    for (const type of candidates) {
        if (type === 'source' && ITEMS_WITH_SOURCE.length === 0) continue;

        if (type === 'source') {
            const item = pickRandom(ITEMS_WITH_SOURCE);
            return { type, item, target: { lat: item.source.lat, lon: item.source.lon } };
        }
        if (type === 'destination') {
            const item = pickRandom(ITEMS);
            return { type, item, options: makeOptions(item.destination, ALL_DESTINATIONS) };
        }
        if (type === 'longest') {
            const rivers = pickLongestSet();
            let winnerIdx = 0;
            for (let i = 1; i < rivers.length; i++) {
                if (rivers[i].lengthCz > rivers[winnerIdx].lengthCz) winnerIdx = i;
            }
            return { type, rivers, winnerIdx };
        }
    }
    return null;
};

const RiversGame = () => {
    const { features, error } = useKrajFeatures();
    const [round, setRound] = useState(1);
    const [score, setScore] = useState(0);
    const [question, setQuestion] = useState(makeQuestion);
    const [selectedAnswer, setSelectedAnswer] = useState(null);
    const [guessPoint, setGuessPoint] = useState(null);
    const [mapResult, setMapResult] = useState(null);
    const [modalVisible, setModalVisible] = useState(false);
    const [rounds, setRounds] = useState([]);
    const [finished, setFinished] = useState(false);

    const maxScore = EXTENDED_TOTAL_ROUNDS * 100;

    const restart = () => {
        setRound(1);
        setScore(0);
        setQuestion(makeQuestion());
        setSelectedAnswer(null);
        setGuessPoint(null);
        setMapResult(null);
        setModalVisible(false);
        setRounds([]);
        setFinished(false);
    };

    if (finished || !question) {
        return (
            <QuizFinalResult score={score} totalRounds={EXTENDED_TOTAL_ROUNDS} rounds={rounds} onRestart={restart} />
        );
    }

    const winnerRiver = question.type === 'longest' ? question.rivers[question.winnerIdx] : null;
    const correctAnswer = question.type === 'destination' ? question.item.destination : null;

    const labelForRound = () => {
        if (question.type === 'longest') return `Nejdelší: ${winnerRiver.name}`;
        return question.item ? `${question.item.name} (${formatLength(question.item.lengthCz)})` : '';
    };

    const answerOption = option => {
        if (selectedAnswer) return;
        setSelectedAnswer(option);
        const correct = option === correctAnswer;
        if (correct) setScore(s => s + 100);
        setRounds(prev => [...prev, { correct, score: correct ? 100 : 0, label: labelForRound() }]);
        setModalVisible(true);
    };

    const pickLongestRiver = riverIdx => {
        if (selectedAnswer) return;
        setSelectedAnswer(String(riverIdx));
        const correct = riverIdx === question.winnerIdx;
        if (correct) setScore(s => s + 100);
        setRounds(prev => [...prev, { correct, score: correct ? 100 : 0, label: labelForRound() }]);
        setModalVisible(true);
    };

    const submitMapGuess = () => {
        if (!guessPoint || mapResult) return;
        const distance = calculateDistanceKm(guessPoint, question.target);
        const roundScore = calculateBlindMapScore(distance);
        setMapResult({ distance, score: roundScore });
        setScore(s => s + roundScore);
        setRounds(prev => [
            ...prev,
            { correct: roundScore >= CORRECT_SCORE_THRESHOLD, score: roundScore, label: labelForRound() },
        ]);
        setModalVisible(true);
    };

    const advanceRound = () => {
        setModalVisible(false);
        setRound(r => r + 1);
        setQuestion(makeQuestion());
        setSelectedAnswer(null);
        setGuessPoint(null);
        setMapResult(null);
    };

    const showFinal = () => {
        setModalVisible(false);
        setFinished(true);
    };

    const correctForModal =
        question.type === 'longest'
            ? selectedAnswer === String(question.winnerIdx)
            : question.type === 'source' && mapResult
            ? mapResult.score >= CORRECT_SCORE_THRESHOLD
            : selectedAnswer === correctAnswer;

    const wrongVerdictForModal = (() => {
        if (question.type === 'longest' && selectedAnswer && !correctForModal) {
            const picked = question.rivers[Number(selectedAnswer)];
            return `${picked.name} není nejdelší :(`;
        }
        if (question.type === 'source' && mapResult) {
            return `${question.item.name} jsi netrefil :(`;
        }
        if (selectedAnswer && selectedAnswer !== correctAnswer) {
            return `${selectedAnswer} není správná odpověď :(`;
        }
        return null;
    })();

    if (question.type === 'longest') {
        return (
            <>
                <QuizPanel>
                    <RoundStatus round={round} score={score} maxScore={maxScore} totalRounds={EXTENDED_TOTAL_ROUNDS} />
                    <Question>
                        <p>České řeky</p>
                        <h2>Která řeka je v Česku nejdelší?</h2>
                        <p>Délka platí pro úsek na území ČR.</p>
                    </Question>
                    <RankCardStack>
                        {question.rivers.map((river, idx) => {
                            const idxStr = String(idx);
                            const state = !selectedAnswer
                                ? null
                                : idx === question.winnerIdx
                                ? 'correct'
                                : selectedAnswer === idxStr
                                ? 'wrong'
                                : null;
                            return (
                                <RankCard
                                    key={`${idx}-${river.rank}-${river.name}`}
                                    type="button"
                                    $state={state}
                                    $disabled={!!selectedAnswer}
                                    onClick={() => pickLongestRiver(idx)}
                                >
                                    <RankCardContent>
                                        <QuizPhotoThumbnail item={river} alt={river.name} />
                                        <RankCardCopy>
                                            <RankCardName>{river.name}</RankCardName>
                                            <RankCardMeta>
                                                Cíl toku: {river.destination}
                                                {selectedAnswer ? (
                                                    <>
                                                        {' '}
                                                        • <Length>{formatLength(river.lengthCz)}</Length>
                                                    </>
                                                ) : (
                                                    ''
                                                )}
                                            </RankCardMeta>
                                        </RankCardCopy>
                                    </RankCardContent>
                                </RankCard>
                            );
                        })}
                    </RankCardStack>
                </QuizPanel>
                <RoundResultModal
                    visible={modalVisible}
                    correct={correctForModal}
                    round={round}
                    totalRounds={EXTENDED_TOTAL_ROUNDS}
                    onNext={advanceRound}
                    onShowResult={showFinal}
                    wrongVerdict={wrongVerdictForModal}
                >
                    <h3>Správná odpověď</h3>
                    <ModalPhoto item={winnerRiver} alt={winnerRiver.name} />
                    <p>
                        Nejdelší byla <strong style={{ fontSize: '1.2em' }}>{winnerRiver.name}</strong> (
                        {formatLength(winnerRiver.lengthCz)}).
                    </p>
                    <p>Cíl toku: {winnerRiver.destination}.</p>
                </RoundResultModal>
            </>
        );
    }

    const item = question.item;

    return (
        <>
            <QuizPanel>
                <RoundStatus round={round} score={score} maxScore={maxScore} totalRounds={EXTENDED_TOTAL_ROUNDS} />

                {question.type === 'destination' && (
                    <>
                        <Question>
                            <p>Česká řeka ({formatLength(item.lengthCz)})</p>
                            <h2>{item.name}</h2>
                            <p>Kam tato řeka ústí? (řeka nebo moře)</p>
                        </Question>
                        <QuizPhotoPreview item={item} alt={item.name} label="Fotka řeky" />
                        <OptionGrid>
                            {question.options.map(option => (
                                <OptionButton
                                    key={option}
                                    answer={option}
                                    correctAnswer={correctAnswer}
                                    disabled={!!selectedAnswer}
                                    selectedAnswer={selectedAnswer}
                                    onClick={() => answerOption(option)}
                                />
                            ))}
                        </OptionGrid>
                    </>
                )}

                {question.type === 'source' && (
                    <>
                        <Question>
                            <p>Slepá mapa pramenů ({formatLength(item.lengthCz)})</p>
                            <h2>{item.name}</h2>
                            <p>Klikni do mapy, kde tato řeka pramení.</p>
                        </Question>
                        <QuizPhotoPreview item={item} alt={item.name} label="Fotka řeky" />
                        {error && (
                            <Alert type="error" message="Mapa se nepodařila načíst" description={error.message} />
                        )}
                        <MapFrame>
                            {features ? (
                                <SvgMap
                                    features={features}
                                    guessPoint={guessPoint}
                                    targetPoint={mapResult ? question.target : null}
                                    onMapClick={mapResult ? null : setGuessPoint}
                                />
                            ) : (
                                <Alert type="info" message="Načítám mapu..." />
                            )}
                        </MapFrame>
                        {!mapResult && (
                            <Space wrap>
                                <Button type="primary" disabled={!guessPoint} onClick={submitMapGuess}>
                                    Vyhodnotit
                                </Button>
                                {guessPoint && <Tag>Tip umístěn</Tag>}
                            </Space>
                        )}
                    </>
                )}
            </QuizPanel>

            <RoundResultModal
                visible={modalVisible}
                correct={correctForModal}
                round={round}
                totalRounds={EXTENDED_TOTAL_ROUNDS}
                onNext={advanceRound}
                onShowResult={showFinal}
                wrongVerdict={wrongVerdictForModal}
            >
                <h3>Správná odpověď</h3>
                <ModalPhoto item={item} alt={item.name} />
                <p>
                    <strong style={{ fontSize: '1.2em' }}>{item.name}</strong>
                    <br />
                    Délka: {formatLength(item.lengthCz)} • Pramen u: {item.sourceText || '?'} • Cíl toku:{' '}
                    {item.destination}
                </p>
                {question.type === 'source' && mapResult && (
                    <>
                        <p>
                            Tvůj tip byl <strong>{formatDistance(mapResult.distance)}</strong> od pramene ·{' '}
                            <strong>{mapResult.score}</strong> bodů za kolo.
                        </p>
                        <ModalMapPreview features={features} guessPoint={guessPoint} targetPoint={question.target} />
                    </>
                )}
                {question.type === 'destination' && selectedAnswer && selectedAnswer !== correctAnswer && (
                    <p>
                        Tvůj tip byl: <strong>{selectedAnswer}</strong>.
                    </p>
                )}
            </RoundResultModal>
        </>
    );
};

export const Rivers = () => (
    <Content>
        <Page>
            <Header>
                <BackToModesLink />
                <h1>Řeky</h1>
                <p>
                    100 nejdelších řek protékajících Českem. Pět kol — slepá mapa pramenů, kam řeka ústí, nebo porovnání
                    délky tří řek. Data: Wikipedie + Wikipedia coordinates API.
                </p>
            </Header>
            <RiversGame />
        </Page>
    </Content>
);
